// src/pages/HealthEditPage.js
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import api from "../api";
import { useNavGuard } from "../hooks/useNavGuard";
import NavGuardModal from "../components/NavGuardModal";
import AutoGrowTextarea from "../components/AutoGrowTextarea";

const INSURANCE_COMPANY_SUGGESTIONS = [
  "Blue Cross Blue Shield", "Medicaid", "Medicare", "Priority Health",
  "Molina", "Aetna", "Cigna", "UnitedHealthcare", "Tricare", "Veterans", "Other",
];

// insurance_company is stored as a comma-separated string; treat it as a list.
function splitInsurers(s) {
  return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
}

const FAMILY_MEMBERS_ENDPOINT = (id) => `/clients/${id}/family-members`;

function ynValue(v) {
  if (v === true)  return "yes";
  if (v === false) return "no";
  return "";
}

function ynToBool(v) {
  if (v === "yes") return true;
  if (v === "no")  return false;
  return null;
}

function safeParseJson(s) {
  try { return s ? JSON.parse(s) : null; } catch { return null; }
}

function memberLabel(m) {
  const name = `${m?.first_name || ""} ${m?.last_name || ""}`.trim();
  return name || m?.full_name || m?.name || `Member ${m?.id}`;
}

export default function HealthEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [successMsg, setSuccess] = useState("");
  const [dirty, setDirty]       = useState(false);
  const [version, setVersion]   = useState(null);  // optimistic-lock token

  const [familyMembers, setFamilyMembers] = useState([]);
  const [client, setClient] = useState(null);

  const [hasHouseholdMedicalIssues, setHasHouseholdMedicalIssues] = useState("");
  const [householdMedicalIssues, setHouseholdMedicalIssues]       = useState("");
  const [takesMedicationRegularly, setTakesMedicationRegularly]   = useState("");
  const [medicationNotes, setMedicationNotes]                     = useState("");
  const [hasMedicalInsurance, setHasMedicalInsurance]             = useState("");
  const [insuranceCompanies, setInsuranceCompanies]              = useState([]);
  const [coveredMemberIds, setCoveredMemberIds]                   = useState([]);
  const [insuranceCoverageFreeText, setInsuranceCoverageFreeText] = useState("");
  const [receivedCounseling, setReceivedCounseling]               = useState("");
  const [counselingDetails, setCounselingDetails]                 = useState("");
  const [counselorName, setCounselorName]                         = useState("");
  const [counselorPhone, setCounselorPhone]                       = useState("");
  const [recentTrauma, setRecentTrauma]                           = useState("");
  const [traumaDetails, setTraumaDetails]                         = useState("");
  const [alcoholOrDrugProblem, setAlcoholOrDrugProblem]           = useState("");
  const [alcoholDrugDetails, setAlcoholDrugDetails]               = useState("");

  const { showModal, resolveGuard } = useNavGuard(dirty);

  // Focus helpers
  const refHouseholdIssuesText = useRef(null);
  const refTakesMedsYes        = useRef(null);
  const refMedicationNotes     = useRef(null);
  const refInsuranceYes        = useRef(null);
  const refInsuranceCompany    = useRef(null);
  const refCoveredMembers      = useRef(null);
  const refCounselingYes       = useRef(null);
  const refCounselingDetails   = useRef(null);
  const refTraumaYes           = useRef(null);
  const refTraumaDetails       = useRef(null);
  const refAlcoholYes          = useRef(null);
  const refAlcoholDetails      = useRef(null);
  const refSaveBtn             = useRef(null);

  function deferFocus(r) { requestAnimationFrame(() => r?.current?.focus()); }

  // Mark dirty helper — call instead of plain setter when user changes a field
  function mark() { setDirty(true); setSuccess(""); }

  const familyMemberOptions = useMemo(() => {
    // "Who is covered" lists real, named people: the client plus every family
    // member (spouse, child, boyfriend, etc. — each by name). The legacy
    // synthetic "spouse" option only appears when a spouse_name is on file;
    // otherwise the spouse is just a family member entry.
    const clientName = `${client?.first_name || ""} ${client?.last_name || ""}`.trim();
    const spouseName = (client?.spouse_name || "").trim();
    const base = [{ id: "client", label: clientName || "Client" }];
    if (spouseName) base.push({ id: "spouse", label: spouseName });
    const fromApi = (familyMembers || []).map((m) => ({ id: String(m.id), label: memberLabel(m) }));
    const seen = new Set();
    return [...base, ...fromApi].filter(({ id }) => { if (seen.has(id)) return false; seen.add(id); return true; });
  }, [familyMembers, client]);

  // Suggestions plus any already-saved value not in the list (e.g. legacy
  // free-typed insurers), so existing selections aren't dropped.
  const insurerOptions = useMemo(() => {
    const set = new Set(INSURANCE_COMPANY_SUGGESTIONS);
    insuranceCompanies.forEach((c) => set.add(c));
    // Alphabetical, but keep "Other" pinned to the bottom.
    return Array.from(set).sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
  }, [insuranceCompanies]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true); setError(""); setSuccess("");
      try {
        try {
          const fmRes = await api.get(FAMILY_MEMBERS_ENDPOINT(id));
          const d = fmRes?.data || {};
          if (alive) setFamilyMembers(Array.isArray(d) ? d : Array.isArray(d.items) ? d.items : []);
        } catch { if (alive) setFamilyMembers([]); }

        try {
          const cRes = await api.get(`/clients/${id}`);
          if (alive) setClient(cRes?.data || null);
        } catch { if (alive) setClient(null); }

        const res  = await api.get(`/clients/${id}/medical`);
        const item = res?.data?.item || null;
        if (!alive) return;

        if (item) {
          setHasHouseholdMedicalIssues(ynValue(item.has_household_medical_issues));
          setHouseholdMedicalIssues(item.household_medical_issues || "");
          setTakesMedicationRegularly(ynValue(item.takes_medication_regularly));
          setMedicationNotes(item.medication_notes || "");
          setHasMedicalInsurance(ynValue(item.has_medical_insurance));
          setInsuranceCompanies(splitInsurers(item.insurance_company));
          const parsed = safeParseJson(item.insurance_coverage_details);
          setCoveredMemberIds((Array.isArray(parsed?.covered_member_ids) ? parsed.covered_member_ids : []).map(String));
          setInsuranceCoverageFreeText(typeof parsed?.notes === "string" ? parsed.notes : "");
          setReceivedCounseling(ynValue(item.received_counseling));
          setCounselingDetails(item.counseling_details || "");
          setCounselorName(item.counselor_name || "");
          setCounselorPhone(item.counselor_phone || "");
          setRecentTrauma(ynValue(item.recent_trauma));
          setTraumaDetails(item.trauma_details || "");
          setAlcoholOrDrugProblem(ynValue(item.alcohol_or_drug_problem));
          setAlcoholDrugDetails(item.alcohol_drug_details || "");
          setVersion(item.version ?? null);
        }
        setDirty(false);
      } catch (_) {
        // silently fall back — don't block page
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [id]);

  async function doSave() {
    setSaving(true); setError(""); setSuccess("");
    try {
      const insuranceCoverageDetailsJson =
        hasMedicalInsurance === "yes"
          ? JSON.stringify({ covered_member_ids: coveredMemberIds, notes: insuranceCoverageFreeText || "" })
          : null;

      const res = await api.put(`/clients/${id}/medical`, {
        version: version,
        has_household_medical_issues: ynToBool(hasHouseholdMedicalIssues),
        household_medical_issues: hasHouseholdMedicalIssues === "yes" ? householdMedicalIssues || null : null,
        takes_medication_regularly: ynToBool(takesMedicationRegularly),
        medication_notes: takesMedicationRegularly === "yes" ? medicationNotes || null : null,
        has_medical_insurance: ynToBool(hasMedicalInsurance),
        insurance_company: hasMedicalInsurance === "yes" ? (insuranceCompanies.join(", ") || null) : null,
        insurance_coverage_details: insuranceCoverageDetailsJson,
        received_counseling: ynToBool(receivedCounseling),
        counseling_details: receivedCounseling === "yes" ? counselingDetails || null : null,
        counselor_name: receivedCounseling === "yes" ? counselorName || null : null,
        counselor_phone: receivedCounseling === "yes" ? counselorPhone || null : null,
        recent_trauma: ynToBool(recentTrauma),
        trauma_details: recentTrauma === "yes" ? traumaDetails || null : null,
        alcohol_or_drug_problem: ynToBool(alcoholOrDrugProblem),
        alcohol_drug_details: alcoholOrDrugProblem === "yes" ? alcoholDrugDetails || null : null,
      });

      setVersion(res?.data?.item?.version ?? version);
      setDirty(false);
      setSuccess("Health info saved.");
      return true;
    } catch (e) {
      if (e?.response?.status === 409) {
        setError("This client's health info was changed by someone else since you opened this page. Please reload and re-enter your changes.");
      } else {
        setError(e?.response?.data?.error || e?.message || "Failed to save health info.");
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div className="p-3"><Spinner animation="border" size="sm" className="me-2" />Loading health...</div>
  );

  return (
    <div className="p-3">
      <NavGuardModal
        show={showModal}
        saving={saving}
        error={error}
        onSave={async () => { const ok = await doSave(); if (ok) resolveGuard(true); }}
        onDiscard={() => { setDirty(false); resolveGuard(true); }}
        onCancel={() => resolveGuard(false)}
      />

      <Form onSubmit={async (e) => { e.preventDefault(); const ok = await doSave(); if (ok) navigate(`/clients/${id}/view/health`); }}>
        <Row className="mb-3">
          <Col>
            <h3 className="mb-0">
              Edit Health
              {dirty && <span className="text-warning ms-2" style={{ fontSize: "0.9rem" }}>● Unsaved</span>}
            </h3>
            <div className="text-muted">Client ID: {id}</div>
          </Col>
          <Col className="d-flex justify-content-end align-items-start gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)} disabled={saving}>Back</Button>
            <Button ref={refSaveBtn} variant="primary" type="submit" disabled={saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : "Save"}
            </Button>
          </Col>
        </Row>

        {error && !showModal ? <Alert variant="danger" className="mb-3">{error}</Alert> : null}
        {successMsg ? <Alert variant="success" className="mb-3">{successMsg}</Alert> : null}

        {/* 1) Household medical issues */}
        <Card className="mb-3">
          <Card.Header>Household medical, hearing, or vision issues</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Does anyone in your household have medical, hearing, or vision issues?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check type="radio" id="hh-med-yes" name="has_household_medical_issues" label="Yes" value="yes"
                  checked={hasHouseholdMedicalIssues === "yes"}
                  onChange={(e) => { setHasHouseholdMedicalIssues(e.target.value); mark(); deferFocus(refHouseholdIssuesText); }} />
                <Form.Check type="radio" id="hh-med-no" name="has_household_medical_issues" label="No" value="no"
                  checked={hasHouseholdMedicalIssues === "no"}
                  onChange={(e) => { setHasHouseholdMedicalIssues(e.target.value); setHouseholdMedicalIssues(""); mark(); deferFocus(refTakesMedsYes); }} />
              </div>
            </Form.Group>
            {hasHouseholdMedicalIssues === "yes" && (
              <Form.Group>
                <Form.Label>Explain</Form.Label>
                <AutoGrowTextarea ref={refHouseholdIssuesText} rows={3} value={householdMedicalIssues}
                  onChange={(e) => { setHouseholdMedicalIssues(e.target.value); mark(); }} />
              </Form.Group>
            )}
          </Card.Body>
        </Card>

        {/* 2) Medication */}
        <Card className="mb-3">
          <Card.Header>Medication</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Does anyone in your household take medication regularly?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check ref={refTakesMedsYes} type="radio" id="meds-yes" name="takes_medication_regularly" label="Yes" value="yes"
                  checked={takesMedicationRegularly === "yes"}
                  onChange={(e) => { setTakesMedicationRegularly(e.target.value); mark(); deferFocus(refMedicationNotes); }} />
                <Form.Check type="radio" id="meds-no" name="takes_medication_regularly" label="No" value="no"
                  checked={takesMedicationRegularly === "no"}
                  onChange={(e) => { setTakesMedicationRegularly(e.target.value); setMedicationNotes(""); mark(); deferFocus(refInsuranceYes); }} />
              </div>
            </Form.Group>
            {takesMedicationRegularly === "yes" && (
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <AutoGrowTextarea ref={refMedicationNotes} rows={3} value={medicationNotes}
                  onChange={(e) => { setMedicationNotes(e.target.value); mark(); }} />
              </Form.Group>
            )}
          </Card.Body>
        </Card>

        {/* 3) Medical insurance */}
        <Card className="mb-3">
          <Card.Header>Medical Insurance</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Do you have medical insurance?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check ref={refInsuranceYes} type="radio" id="ins-yes" name="has_medical_insurance" label="Yes" value="yes"
                  checked={hasMedicalInsurance === "yes"}
                  onChange={(e) => { setHasMedicalInsurance(e.target.value); mark(); deferFocus(refInsuranceCompany); }} />
                <Form.Check type="radio" id="ins-no" name="has_medical_insurance" label="No" value="no"
                  checked={hasMedicalInsurance === "no"}
                  onChange={(e) => { setHasMedicalInsurance(e.target.value); setInsuranceCompanies([]); setCoveredMemberIds([]); setInsuranceCoverageFreeText(""); mark(); deferFocus(refCounselingYes); }} />
              </div>
            </Form.Group>
            {hasMedicalInsurance === "yes" && (
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Insurance Company</Form.Label>
                    <div ref={refInsuranceCompany} tabIndex={-1} className="border rounded p-2"
                      style={{ maxHeight: 160, overflowY: "auto" }}>
                      {insurerOptions.map((s) => (
                        <Form.Check key={s} type="checkbox" id={`insurer-${s}`} label={s}
                          checked={insuranceCompanies.includes(s)}
                          onChange={(e) => {
                            setInsuranceCompanies((prev) => e.target.checked ? [...prev, s] : prev.filter((x) => x !== s));
                            mark();
                          }} />
                      ))}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Which household members are covered?</Form.Label>
                    <div ref={refCoveredMembers} tabIndex={-1} className="border rounded p-2"
                      style={{ maxHeight: 160, overflowY: "auto" }}>
                      {familyMemberOptions.map((m) => {
                        const val = String(m.id);
                        return (
                          <Form.Check key={val} type="checkbox" id={`covered-${val}`} label={m.label}
                            checked={coveredMemberIds.includes(val)}
                            onChange={(e) => {
                              setCoveredMemberIds((prev) => e.target.checked ? [...prev, val] : prev.filter((x) => x !== val));
                              mark();
                            }} />
                        );
                      })}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Coverage details (optional)</Form.Label>
                    <AutoGrowTextarea rows={3} value={insuranceCoverageFreeText}
                      onChange={(e) => { setInsuranceCoverageFreeText(e.target.value); mark(); }} placeholder="Optional notes about coverage" />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* 4) Counseling */}
        <Card className="mb-3">
          <Card.Header>Counseling</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Have you ever received any counseling?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check ref={refCounselingYes} type="radio" id="coun-yes" name="received_counseling" label="Yes" value="yes"
                  checked={receivedCounseling === "yes"}
                  onChange={(e) => { setReceivedCounseling(e.target.value); mark(); deferFocus(refCounselingDetails); }} />
                <Form.Check type="radio" id="coun-no" name="received_counseling" label="No" value="no"
                  checked={receivedCounseling === "no"}
                  onChange={(e) => { setReceivedCounseling(e.target.value); setCounselingDetails(""); setCounselorName(""); setCounselorPhone(""); mark(); deferFocus(refTraumaYes); }} />
              </div>
            </Form.Group>
            {receivedCounseling === "yes" && (
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Explain</Form.Label>
                    <AutoGrowTextarea ref={refCounselingDetails} rows={3} value={counselingDetails}
                      onChange={(e) => { setCounselingDetails(e.target.value); mark(); }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Counselor Name</Form.Label>
                    <Form.Control type="text" value={counselorName}
                      onChange={(e) => { setCounselorName(e.target.value); mark(); }} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Counselor Phone</Form.Label>
                    <Form.Control type="text" value={counselorPhone}
                      onChange={(e) => { setCounselorPhone(e.target.value); mark(); }} />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* 5) Recent trauma */}
        <Card className="mb-3">
          <Card.Header>Recent Losses / Trauma</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Have you experienced any recent losses or serious traumas in the past six months (death, divorce, accident, loss of job)?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check ref={refTraumaYes} type="radio" id="trauma-yes" name="recent_trauma" label="Yes" value="yes"
                  checked={recentTrauma === "yes"}
                  onChange={(e) => { setRecentTrauma(e.target.value); mark(); deferFocus(refTraumaDetails); }} />
                <Form.Check type="radio" id="trauma-no" name="recent_trauma" label="No" value="no"
                  checked={recentTrauma === "no"}
                  onChange={(e) => { setRecentTrauma(e.target.value); setTraumaDetails(""); mark(); deferFocus(refAlcoholYes); }} />
              </div>
            </Form.Group>
            {recentTrauma === "yes" && (
              <Form.Group>
                <Form.Label>Explain</Form.Label>
                <AutoGrowTextarea ref={refTraumaDetails} rows={3} value={traumaDetails}
                  onChange={(e) => { setTraumaDetails(e.target.value); mark(); }} />
              </Form.Group>
            )}
          </Card.Body>
        </Card>

        {/* 6) Alcohol/drug */}
        <Card className="mb-3">
          <Card.Header>Alcohol / Drug Use</Card.Header>
          <Card.Body>
            <Form.Group className="mb-2">
              <Form.Label>Do you or have you in the past had a problem with alcohol or drug use?</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check ref={refAlcoholYes} type="radio" id="ad-yes" name="alcohol_or_drug_problem" label="Yes" value="yes"
                  checked={alcoholOrDrugProblem === "yes"}
                  onChange={(e) => { setAlcoholOrDrugProblem(e.target.value); mark(); deferFocus(refAlcoholDetails); }} />
                <Form.Check type="radio" id="ad-no" name="alcohol_or_drug_problem" label="No" value="no"
                  checked={alcoholOrDrugProblem === "no"}
                  onChange={(e) => { setAlcoholOrDrugProblem(e.target.value); setAlcoholDrugDetails(""); mark(); deferFocus(refSaveBtn); }} />
              </div>
            </Form.Group>
            {alcoholOrDrugProblem === "yes" && (
              <Form.Group>
                <Form.Label>Explain</Form.Label>
                <AutoGrowTextarea ref={refAlcoholDetails} rows={3} value={alcoholDrugDetails}
                  onChange={(e) => { setAlcoholDrugDetails(e.target.value); mark(); }} />
              </Form.Group>
            )}
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
}
